import { useMemo, useRef, useState } from 'react';
import { useElementWidth } from './useElementWidth';

export interface BlockPoint {
  d: string;
  ndvi: number | null;
  ndwi: number | null;
  ndmi: number | null;
  ndre: number | null;
}

export type BlockSeriesKey = 'ndvi' | 'ndwi' | 'ndmi' | 'ndre';

interface Props {
  points: BlockPoint[];
  /** Which series to draw, in draw order; the last one is drawn heaviest. */
  keys: BlockSeriesKey[];
  height?: number;
}

const PADDING = { top: 16, right: 16, bottom: 26, left: 44 };

/**
 * Nine years of passes on one field, one line per index.
 *
 * Two decisions carry the whole chart.
 *
 * A gap is drawn as a gap. Cloud takes weeks out of a monsoon, and joining the
 * pass before to the pass after would draw a straight line through a month
 * nobody measured - which is exactly the shape a reader trusts most. Any break
 * longer than 45 days ends the stroke and a new one begins.
 *
 * The vertical scale is fixed at -0.6 to 1.0 rather than fitted to the field.
 * Fitting makes every field look equally variable; fixed means a flat line is
 * flat, and two fields can be compared by eye.
 */
export const BLOCK_SERIES: Record<BlockSeriesKey, { label: string; colour: string; weight: number }> = {
  ndvi: { label: 'Crop health', colour: 'var(--series-1)', weight: 1.8 },
  ndwi: { label: 'Standing water', colour: 'var(--series-2)', weight: 1.2 },
  ndmi: { label: 'Canopy moisture', colour: 'var(--series-3)', weight: 1.2 },
  ndre: { label: 'Nitrogen signal', colour: 'var(--series-4)', weight: 1.2 },
};

const LO = -0.6;
const HI = 1.0;
const GAP_MS = 45 * 24 * 3600 * 1000;

export function BlockHistory({ points, keys, height = 190 }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);
  const [hover, setHover] = useState<number | null>(null);

  const plotWidth = Math.max(0, width - PADDING.left - PADDING.right);
  const plotHeight = height - PADDING.top - PADDING.bottom;

  const scale = useMemo(() => {
    const times = points.map((p) => new Date(p.d).getTime());
    const minT = times.length ? Math.min(...times) : 0;
    const maxT = times.length ? Math.max(...times) : 1;
    const spanT = maxT - minT || 1;
    return {
      minT,
      maxT,
      x: (t: number) => PADDING.left + ((t - minT) / spanT) * plotWidth,
      y: (v: number) => PADDING.top + (1 - (v - LO) / (HI - LO)) * plotHeight,
    };
  }, [points, plotWidth, plotHeight]);

  const paths = useMemo(() => {
    const out: Record<string, string> = {};
    for (const key of keys) {
      let d = '';
      let prev: number | null = null;
      for (const p of points) {
        const v = p[key];
        const t = new Date(p.d).getTime();
        if (v === null || v === undefined || Number.isNaN(v)) {
          prev = null;
          continue;
        }
        const cmd = prev === null || t - prev > GAP_MS ? 'M' : 'L';
        d += `${cmd}${scale.x(t).toFixed(1)},${scale.y(v).toFixed(1)}`;
        prev = t;
      }
      out[key] = d;
    }
    return out;
  }, [points, keys, scale]);

  if (!points.length || plotWidth <= 0) {
    return <div className="chart" ref={containerRef} style={{ height }} />;
  }

  const years: number[] = [];
  const y0 = new Date(scale.minT).getFullYear();
  const y1 = new Date(scale.maxT).getFullYear();
  for (let y = y0; y <= y1; y += 1) years.push(y);

  const hoverPoint = hover === null ? null : points[hover];

  return (
    <div className="chart" ref={containerRef}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${keys.map((k) => BLOCK_SERIES[k].label).join(', ')} over ${years.length} years`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const t = scale.minT + ((e.clientX - box.left - PADDING.left) / plotWidth) * (scale.maxT - scale.minT);
          let best = 0;
          let bestD = Infinity;
          points.forEach((p, i) => {
            const dd = Math.abs(new Date(p.d).getTime() - t);
            if (dd < bestD) {
              bestD = dd;
              best = i;
            }
          });
          setHover(best);
        }}
      >
        {[1.0, 0.5, 0.0, -0.5].map((v) => (
          <g key={v}>
            <line
              x1={PADDING.left}
              x2={width - PADDING.right}
              y1={scale.y(v)}
              y2={scale.y(v)}
              className="chart__grid"
            />
            <text x={PADDING.left - 6} y={scale.y(v) + 3} className="chart__axis-text" textAnchor="end">
              {v.toFixed(1)}
            </text>
          </g>
        ))}
        {years.map((y) => {
          const x = scale.x(new Date(`${y}-01-01`).getTime());
          if (x < PADDING.left || x > width - PADDING.right) return null;
          return (
            <g key={y}>
              <line x1={x} x2={x} y1={PADDING.top} y2={height - PADDING.bottom} className="chart__grid" />
              <text x={x} y={height - 8} className="chart__axis-text" textAnchor="middle">
                {`'${String(y).slice(2)}`}
              </text>
            </g>
          );
        })}
        {keys.map((k) => (
          <path
            key={k}
            className="chart__series"
            d={paths[k]}
            stroke={BLOCK_SERIES[k].colour}
            strokeWidth={BLOCK_SERIES[k].weight}
          />
        ))}
        {hoverPoint ? (
          <g>
            <line
              x1={scale.x(new Date(hoverPoint.d).getTime())}
              x2={scale.x(new Date(hoverPoint.d).getTime())}
              y1={PADDING.top}
              y2={height - PADDING.bottom}
              className="chart__crosshair"
            />
            {keys.map((k) =>
              hoverPoint[k] === null || hoverPoint[k] === undefined ? null : (
                <circle
                  key={k}
                  cx={scale.x(new Date(hoverPoint.d).getTime())}
                  cy={scale.y(hoverPoint[k] as number)}
                  r={2.6}
                  fill={BLOCK_SERIES[k].colour}
                />
              ),
            )}
          </g>
        ) : null}
      </svg>
      <p className="chart__caption">
        {hoverPoint ? (
          <>
            <strong>{hoverPoint.d}</strong>
            {keys.map((k) =>
              hoverPoint[k] === null || hoverPoint[k] === undefined ? null : (
                <span key={k}>
                  {' · '}
                  {BLOCK_SERIES[k].label} {(hoverPoint[k] as number).toFixed(2)}
                </span>
              ),
            )}
          </>
        ) : (
          <>
            {points.length} passes read between {points[0]?.d} and {points[points.length - 1]?.d}. A
            break in a line is cloud — nothing is filled in.
          </>
        )}
      </p>
    </div>
  );
}
